import React, { useEffect, useMemo, useRef, useState } from "react";
import {
	Button,
	Flex,
	FormControl,
	FormLabel,
	Input,
	Modal,
	ModalBody,
	ModalCloseButton,
	ModalContent,
	ModalFooter,
	ModalHeader,
	ModalOverlay,
	Textarea,
} from "@chakra-ui/react";
import { Help } from "newui/components/ui/Help";
import { buildQuoteTableSteps } from "guides/features/QuoteTable";
import { ConfirmationDialogs } from "./ConfirmationDialogs";
import { SpreadsheetGrid, useColumnManager } from "../SpreadsheetEditor";
import { TemplateSelector } from "./TemplateSelector";
import { BLANK_ROWS, PRESET_OPTIONS } from "./quote-table-constants";
import type {
	ColumnConfig,
	DefaultRowConfig,
	QuoteTableConfig,
} from "./quote-table.types";
import type { QuoteTemplate } from "../../api/quote-templates-api";

// Re-export so consumers importing from this module path keep working.
export type { ColumnConfig, DefaultRowConfig, QuoteTableConfig };

export interface UnitOption {
	label: string;
	value: number;
}

export interface CreateQuoteTableModalProps {
	existingColumns?: ColumnConfig[];
	existingDescription?: string;
	existingTableName?: string;
	isOpen: boolean;
	isSubmitting?: boolean;
	templates?: QuoteTemplate[];
	unitOptions?: UnitOption[];
	onClose: () => void;
	onSubmit: (config: QuoteTableConfig) => void;
}

/**
 * Orchestrator for the quote-table editor modal. Composes `TemplateSelector`,
 * `SpreadsheetGrid`, name/description inputs, and `ConfirmationDialogs` around
 * the `useColumnManager` hook.
 *
 * This file replaces the 897-line monolith — all column state, type
 * constraints, preset application, and cascade-delete logic now live in
 * `useColumnManager`. The modal is purely orchestration: wires hook callbacks
 * to sub-components and owns the local UI state for name/description, preset
 * selection, and the two confirmation dialogs.
 *
 * The output `QuoteTableConfig` shape (and the `CreateQuoteTableModalProps`
 * surface) is preserved exactly so the parent `ProcurerQuoteTab` requires no
 * change.
 */
export const CreateQuoteTableModal = ({
	existingColumns,
	existingDescription,
	existingTableName,
	isOpen,
	isSubmitting,
	templates,
	onClose,
	onSubmit,
}: CreateQuoteTableModalProps) => {
	const {
		addColumn,
		applyPreset,
		columns,
		executeColumnDelete,
		getDisabledReason,
		hasUserModified,
		insertColumn,
		isTypeDisabled,
		moveColumn,
		pendingDeleteId,
		removeColumn,
		reorderColumns,
		resetForCreate,
		resetForEdit,
		updateColumnLabel,
		updateColumnType,
	} = useColumnManager();

	const [name, setName] = useState("");
	const [description, setDescription] = useState("");
	const [selectedPreset, setSelectedPreset] = useState("blank");
	const [defaultRows, setDefaultRows] =
		useState<DefaultRowConfig[]>(BLANK_ROWS);

	const [isPresetWarningOpen, setIsPresetWarningOpen] = useState(false);
	const [pendingPresetKey, setPendingPresetKey] = useState("");
	const [pendingPresetLabel, setPendingPresetLabel] = useState("");

	const [isTotalRemovalWarningOpen, setIsTotalRemovalWarningOpen] =
		useState(false);
	const [pendingDeleteColumnLabel, setPendingDeleteColumnLabel] = useState("");

	const isEditMode = Boolean(existingColumns?.length);

	// Reset everything on the false→true transition of `isOpen`. A ref guards
	// the body so subsequent renders (typing in the inputs, etc.) don't wipe
	// user state. This pattern lets us include all referenced values in the
	// dep array without re-initialising on every parent re-render.
	const wasOpenRef = useRef(false);
	useEffect(() => {
		if (isOpen && !wasOpenRef.current) {
			if (isEditMode) {
				resetForEdit(existingColumns);
				setName(existingTableName ?? "");
				setDescription(existingDescription ?? "");
			} else {
				resetForCreate();
				setName("");
				setDescription("");
				setSelectedPreset("blank");
				setDefaultRows(BLANK_ROWS);
			}
			setIsPresetWarningOpen(false);
			setIsTotalRemovalWarningOpen(false);
		}
		wasOpenRef.current = isOpen;
	}, [
		isOpen,
		isEditMode,
		existingColumns,
		existingTableName,
		existingDescription,
		resetForCreate,
		resetForEdit,
	]);

	const handleRemoveColumn = (localId: string) => {
		const result = removeColumn(localId);
		if (result === "needs-confirmation") {
			const col = columns.find((c) => c.localId === localId);
			setPendingDeleteColumnLabel(col?.label ?? "");
			setIsTotalRemovalWarningOpen(true);
		}
		return result;
	};

	const handleTotalRemovalConfirm = () => {
		if (pendingDeleteId) executeColumnDelete(pendingDeleteId);
		setIsTotalRemovalWarningOpen(false);
	};

	const handleTotalRemovalCancel = () => {
		setIsTotalRemovalWarningOpen(false);
	};

	const applyPresetResultToState = (presetKey: string) => {
		const result = applyPreset(presetKey, templates ?? []);
		setDefaultRows(result.rows);
		if (result.name !== undefined) setName(result.name);
		if (result.description !== undefined) setDescription(result.description);
		setSelectedPreset(presetKey);
	};

	const handlePresetChange = (presetKey: string) => {
		if (hasUserModified) {
			const preset = PRESET_OPTIONS.find((p) => p.value === presetKey);
			setPendingPresetLabel(preset?.label ?? presetKey);
			setPendingPresetKey(presetKey);
			setIsPresetWarningOpen(true);
		} else {
			applyPresetResultToState(presetKey);
		}
	};

	const handlePresetConfirm = () => {
		applyPresetResultToState(pendingPresetKey);
		setIsPresetWarningOpen(false);
	};

	const handlePresetCancel = () => {
		setIsPresetWarningOpen(false);
	};

	const guideSteps = useMemo(() => buildQuoteTableSteps(), []);
	const isSubmitDisabled =
		name.trim().length === 0 || description.trim().length === 0;

	const handleSubmit = () => {
		const config: QuoteTableConfig = {
			name: name.trim() || "Quote Table",
			description: description.trim(),
			columns,
			presetType: selectedPreset || "custom",
			defaultRows,
		};
		onSubmit(config);
	};

	return (
		<Modal isOpen={isOpen} onClose={onClose} size="6xl" isCentered>
			<ModalOverlay />
			<ModalContent minW="min(900px, 95vw)" maxW="95vw">
				<ModalHeader>
					<Flex align="center" gap={2}>
						<span>
							{isEditMode ? "Edit Quote Table" : "Create Quote Table"}
						</span>
						<Help
							guideSteps={guideSteps}
							label="Show me how the quote table editor works"
							data-testid="quote-table-help"
						/>
					</Flex>
				</ModalHeader>
				<ModalCloseButton />
				<ModalBody>
					<TemplateSelector
						isEditMode={isEditMode}
						selectedPreset={selectedPreset}
						templates={templates ?? []}
						onPresetChange={handlePresetChange}
						data-test-id="quote-table-template"
					/>

					<SpreadsheetGrid
						columns={columns}
						defaultRows={defaultRows}
						getDisabledReason={getDisabledReason}
						isTypeDisabled={isTypeDisabled}
						onAddColumn={addColumn}
						onInsertColumn={insertColumn}
						onMoveColumn={moveColumn}
						onRemoveColumn={handleRemoveColumn}
						onReorderColumns={reorderColumns}
						onUpdateLabel={updateColumnLabel}
						onUpdateType={updateColumnType}
					/>

					<FormControl mt={4} isRequired>
						<FormLabel>Table Name</FormLabel>
						<Input
							data-testid="quote-table-name-input"
							value={name}
							onChange={(e) => setName(e.target.value)}
							placeholder="e.g. Preliminaries"
						/>
					</FormControl>

					<FormControl mt={3} isRequired>
						<FormLabel>Description</FormLabel>
						<Textarea
							data-testid="quote-table-description-input"
							value={description}
							onChange={(e) => setDescription(e.target.value)}
							placeholder="What does this table cover? Suppliers will see this to understand what you're asking for."
							rows={3}
						/>
					</FormControl>

					<ConfirmationDialogs
						isPresetWarningOpen={isPresetWarningOpen}
						isTotalRemovalWarningOpen={isTotalRemovalWarningOpen}
						pendingDeleteColumnLabel={pendingDeleteColumnLabel}
						pendingPresetLabel={pendingPresetLabel}
						onPresetWarningCancel={handlePresetCancel}
						onPresetWarningConfirm={handlePresetConfirm}
						onTotalRemovalCancel={handleTotalRemovalCancel}
						onTotalRemovalConfirm={handleTotalRemovalConfirm}
					/>
				</ModalBody>
				<ModalFooter>
					<Button variant="ghost" onClick={onClose}>
						Cancel
					</Button>
					<Button
						colorScheme="blue"
						ml={3}
						isLoading={isSubmitting}
						isDisabled={isSubmitDisabled}
						onClick={handleSubmit}
					>
						{isEditMode ? "Save Changes" : "Create Table"}
					</Button>
				</ModalFooter>
			</ModalContent>
		</Modal>
	);
};

export default CreateQuoteTableModal;
